import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '../../../shared/lib/utils';
import { useChat } from '../hooks/useChat';
import { Modal } from '../../../shared/ui';
import { toast } from '../../../shared/lib/uiStore';
import { useFeatureBacklog } from '../hooks/useFeatureBacklog';
import type { FeatureRequestInput, FeatureStatus } from '../services/featureBacklog';
import {
  X, Send, Trash2, Bot, User, Loader2, Wrench, Globe,
  CircuitBoard, Package, GitBranch, Search, ChevronDown, ChevronRight, Network, Lightbulb, ClipboardList,
  Sparkles, AlertCircle,
} from 'lucide-react';

const TOOL_ICONS: Record<string, React.ReactNode> = {
  faq_cache_local: <Sparkles className="h-3 w-3" />,
  faq_cache_shared: <Sparkles className="h-3 w-3" />,
  search_wiki_concepts: <Network className="h-3 w-3" />,
  read_wiki_page: <Network className="h-3 w-3" />,
  search_products: <CircuitBoard className="h-3 w-3" />,
  search_components: <Package className="h-3 w-3" />,
  analyze_bom: <Search className="h-3 w-3" />,
  check_inventory: <Package className="h-3 w-3" />,
  search_changes: <GitBranch className="h-3 w-3" />,
  where_used: <Search className="h-3 w-3" />,
  dashboard_summary: <CircuitBoard className="h-3 w-3" />,
  exa_web_search: <Globe className="h-3 w-3" />,
};

const SUGGESTIONS = [
  'Show me the PLM ontology and core entity types',
  'Read the roadmap for the ontology chatbot',
  'What would be affected if a component becomes obsolete?',
  'Find all low-stock components',
  'What change requests are pending review?',
  'Search the web for STM32H743 alternatives',
];

interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
}

type FeatureRequestForm = FeatureRequestInput;

const emptyFeatureRequest: FeatureRequestForm = {
  title: '',
  affectedArea: 'chatbot',
  currentProblem: '',
  requestedFeature: '',
  productivityImpact: '',
  successCriteria: '',
  urgency: 'medium',
};

const FEATURE_AREAS = [
  { value: 'chatbot', label: 'Chatbot and ontology assistant' },
  { value: 'plm', label: 'PLM product and lifecycle workspace' },
  { value: 'pdm', label: 'PDM document and BOM workspace' },
  { value: 'ecm', label: 'ECM change and revision workspace' },
  { value: 'erp', label: 'ERP inventory and sourcing workspace' },
  { value: 'admin', label: 'Admin, roles, and governance' },
  { value: 'cross-module', label: 'Cross-module workflow' },
];

function buildFeatureRequestPrompt(form: FeatureRequestForm, currentPage: string) {
  return `[Feature Request]
Title: ${form.title}
Affected area: ${form.affectedArea}
Current page or context: ${currentPage}
Urgency: ${form.urgency}

Current productivity problem:
${form.currentProblem}

Requested feature:
${form.requestedFeature}

Why this improves productivity:
${form.productivityImpact}

Success criteria:
${form.successCriteria}

Please analyze this feature request against the current implementation.
Structure the answer as:
1. Problem framing
2. Impacted ontology objects and workflows
3. Proposed capability
4. Dependencies or implementation notes
5. Risks or tradeoffs
6. Recommended roadmap placement
7. Immediate next build step`;
}

export function ChatPanel({ open, onClose }: ChatPanelProps) {
  const { messages, isLoading, error, send, clear, hasApiKey } = useChat();
  const { backlog, addFeatureRequest, updateFeatureStatus } = useFeatureBacklog();
  const [input, setInput] = useState('');
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
  const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [featureRequest, setFeatureRequest] = useState<FeatureRequestForm>(emptyFeatureRequest);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    send(input.trim(), { page: location.pathname });
    setInput('');
  };

  const handleSuggestion = (suggestion: string) => {
    send(suggestion, { page: location.pathname });
  };

  const submitFeatureRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureRequest.title.trim() || !featureRequest.currentProblem.trim() || !featureRequest.requestedFeature.trim()) {
      return;
    }

    addFeatureRequest(featureRequest, location.pathname);
    send(buildFeatureRequestPrompt(featureRequest, location.pathname), { page: location.pathname });
    toast.success('Feature request added', 'Saved to the prioritized backlog and sent to the assistant for analysis.');
    setFeatureRequest(emptyFeatureRequest);
    setFeatureRequestOpen(false);
  };

  const backlogCounts = backlog.reduce<Record<string, number>>((accumulator, item) => {
    accumulator[item.priority] = (accumulator[item.priority] || 0) + 1;
    return accumulator;
  }, {});

  const statusTone: Record<FeatureStatus, string> = {
    submitted: 'text-info-500',
    triage: 'text-warning-500',
    planned: 'text-primary-500',
    'in-progress': 'text-secondary-500',
    done: 'text-success-500',
  };

  const toggleToolExpand = (id: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (!open) return null;

  return (
    <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-surface-raised border-l border-border shadow-modal z-50 flex flex-col animate-slide-right">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-primary-600 to-primary-700 flex-shrink-0">
        <div className="flex items-center gap-2 text-white">
          <div className="h-7 w-7 rounded-lg bg-white/20 flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">AI Assistant</h2>
            <p className="text-[10px] text-white/60">Powered by Claude + Exa</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setBacklogOpen(true)}
            className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            title="Prioritized backlog"
          >
            <ClipboardList className="h-4 w-4" />
          </button>
          <button
            onClick={() => setFeatureRequestOpen(true)}
            className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            title="New feature request"
          >
            <Lightbulb className="h-4 w-4" />
          </button>
          <button onClick={clear} className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors" title="Clear chat">
            <Trash2 className="h-4 w-4" />
          </button>
          <button onClick={onClose} className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {!hasApiKey && (
          <div className="rounded-lg bg-warning-500/10 border border-warning-500/20 p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-warning-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-warning-500">API Key Required</p>
              <p className="text-xs text-content-muted mt-0.5">Set <code className="font-mono bg-surface-overlay px-1 rounded">VITE_ANTHROPIC_API_KEY</code> in your <code className="font-mono bg-surface-overlay px-1 rounded">.env</code> file to enable AI features.</p>
            </div>
          </div>
        )}

        {messages.length === 0 && hasApiKey && (
          <div className="text-center py-8">
            <div className="h-12 w-12 rounded-xl bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
              <Bot className="h-6 w-6 text-primary-500" />
            </div>
            <p className="text-sm font-medium text-content mb-1">How can I help?</p>
            <p className="text-xs text-content-muted mb-4">I can search PLM records, traverse ontology relationships, analyze impact, check inventory, and search the web for component info.</p>
            <button
              onClick={() => setBacklogOpen(true)}
              className="mb-2 w-full rounded-md border border-border bg-surface-overlay px-3 py-2 text-left text-xs text-content-secondary transition-colors hover:bg-surface-inset hover:text-content"
            >
              View prioritized feature backlog
            </button>
            <button
              onClick={() => setFeatureRequestOpen(true)}
              className="mb-4 w-full rounded-md border border-primary-500/20 bg-primary-500/8 px-3 py-2 text-left text-xs text-primary-600 transition-colors hover:bg-primary-500/12"
            >
              Submit a feature request for a productivity problem in the current implementation
            </button>
            <div className="space-y-1.5">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSuggestion(s)}
                  className="w-full text-left px-3 py-2 rounded-md text-xs text-content-secondary bg-surface-overlay hover:bg-surface-inset hover:text-content transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={cn('flex gap-2', msg.role === 'user' && 'flex-row-reverse')}>
            <div className={cn(
              'h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5',
              msg.role === 'user' ? 'bg-primary-500/20' : 'bg-surface-overlay'
            )}>
              {msg.role === 'user'
                ? <User className="h-3.5 w-3.5 text-primary-500" />
                : <Bot className="h-3.5 w-3.5 text-content-muted" />}
            </div>
            <div className={cn(
              'max-w-[85%] rounded-lg px-3 py-2 text-sm',
              msg.role === 'user'
                ? 'bg-primary-600 text-white'
                : 'bg-surface-overlay text-content'
            )}>
              {/* Tool calls visualization */}
              {msg.toolCalls && msg.toolCalls.length > 0 && (
                <div className="mb-2 space-y-1">
                  {msg.toolCalls.map((tc, i) => {
                    const toolId = `${msg.id}-${i}`;
                    const expanded = expandedTools.has(toolId);
                    return (
                      <div key={i} className="rounded border border-border-muted bg-surface/50 text-xs">
                        <button
                          onClick={() => toggleToolExpand(toolId)}
                          className="w-full flex items-center gap-1.5 px-2 py-1 hover:bg-surface-overlay/50 transition-colors rounded"
                        >
                          <Wrench className="h-3 w-3 text-content-muted" />
                          {TOOL_ICONS[tc.name] || <Wrench className="h-3 w-3" />}
                          <span className="font-mono text-[10px] text-content-secondary flex-1 text-left">{tc.name}</span>
                          {expanded ? <ChevronDown className="h-3 w-3 text-content-muted" /> : <ChevronRight className="h-3 w-3 text-content-muted" />}
                        </button>
                        {expanded && (
                          <div className="px-2 py-1 border-t border-border-muted">
                            <pre className="text-[10px] text-content-muted overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {JSON.stringify(tc.result?.result || tc.result?.error, null, 2)?.substring(0, 500)}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Message content with basic markdown-like rendering */}
              <div className="whitespace-pre-wrap break-words leading-relaxed">
                {msg.content.split('`').map((part, i) =>
                  i % 2 === 1
                    ? <code key={i} className="font-mono text-xs bg-surface-inset px-1 rounded">{part}</code>
                    : <span key={i}>{part}</span>
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-2">
            <div className="h-6 w-6 rounded-md bg-surface-overlay flex items-center justify-center flex-shrink-0">
              <Bot className="h-3.5 w-3.5 text-content-muted" />
            </div>
            <div className="bg-surface-overlay rounded-lg px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-500" />
              <span className="text-xs text-content-muted">Thinking...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={hasApiKey ? 'Ask about your PLM data...' : 'Configure API key to start'}
            disabled={!hasApiKey || isLoading}
            className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading || !hasApiKey}
            className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[10px] text-content-muted mt-1.5 text-center">
          AI uses ontology and PLM data first, then Exa only for external facts
        </p>
      </form>

      <Modal
        open={featureRequestOpen}
        onClose={() => setFeatureRequestOpen(false)}
        title="Feature Request"
        size="lg"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setFeatureRequestOpen(false)}
              className="rounded-full border border-border bg-surface-raised/40 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-overlay"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="feature-request-form"
              className="rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-500"
            >
              Send to Assistant
            </button>
          </>
        )}
      >
        <form id="feature-request-form" onSubmit={submitFeatureRequest} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-content">Request title</label>
            <input
              value={featureRequest.title}
              onChange={(e) => setFeatureRequest((current) => ({ ...current, title: e.target.value }))}
              placeholder="Improve change impact analysis for released BOMs"
              className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-content">Affected area</label>
              <select
                value={featureRequest.affectedArea}
                onChange={(e) => setFeatureRequest((current) => ({ ...current, affectedArea: e.target.value }))}
                className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {FEATURE_AREAS.map((area) => (
                  <option key={area.value} value={area.value}>{area.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-content">Urgency</label>
              <select
                value={featureRequest.urgency}
                onChange={(e) => setFeatureRequest((current) => ({ ...current, urgency: e.target.value as FeatureRequestForm['urgency'] }))}
                className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content">Current implementation problem</label>
            <textarea
              value={featureRequest.currentProblem}
              onChange={(e) => setFeatureRequest((current) => ({ ...current, currentProblem: e.target.value }))}
              placeholder="Describe the current bottleneck, missing step, or manual workaround."
              rows={4}
              className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content">Requested feature</label>
            <textarea
              value={featureRequest.requestedFeature}
              onChange={(e) => setFeatureRequest((current) => ({ ...current, requestedFeature: e.target.value }))}
              placeholder="Describe the capability that would solve the problem."
              rows={4}
              className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content">Productivity impact</label>
            <textarea
              value={featureRequest.productivityImpact}
              onChange={(e) => setFeatureRequest((current) => ({ ...current, productivityImpact: e.target.value }))}
              placeholder="Explain how this reduces rework, latency, or manual coordination."
              rows={3}
              className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content">Success criteria</label>
            <textarea
              value={featureRequest.successCriteria}
              onChange={(e) => setFeatureRequest((current) => ({ ...current, successCriteria: e.target.value }))}
              placeholder="Define what good looks like after implementation."
              rows={3}
              className="w-full rounded-2xl border border-border bg-surface-raised/84 px-4 py-3 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </form>
      </Modal>

      <Modal
        open={backlogOpen}
        onClose={() => setBacklogOpen(false)}
        title="Prioritized Feature Backlog"
        size="xl"
        footer={(
          <button
            type="button"
            onClick={() => setBacklogOpen(false)}
            className="rounded-full border border-border bg-surface-raised/40 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-overlay"
          >
            Close
          </button>
        )}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {['P0', 'P1', 'P2', 'P3'].map((priority) => (
              <div key={priority} className="rounded-2xl border border-border bg-surface-overlay/45 px-4 py-3">
                <p className="text-xs text-content-muted">{priority}</p>
                <p className="mt-1 text-xl font-semibold text-content">{backlogCounts[priority] || 0}</p>
              </div>
            ))}
          </div>

          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
            {backlog.map((item) => (
              <div key={item.id} className="rounded-[24px] border border-border bg-surface-overlay/45 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary-500/10 px-2.5 py-1 text-[11px] font-semibold text-primary-500">{item.priority}</span>
                      <span className="rounded-full bg-surface px-2.5 py-1 text-[11px] text-content-muted">{item.affectedArea}</span>
                      <span className={cn('text-[11px] font-medium', statusTone[item.status])}>{item.status}</span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-content">{item.title}</p>
                    <p className="mt-2 text-xs leading-5 text-content-secondary">{item.currentProblem}</p>
                    <p className="mt-2 text-xs text-content-muted">Source: {item.source === 'seeded-roadmap' ? 'seeded roadmap' : 'user request'} · Context: {item.currentPage}</p>
                  </div>

                  <select
                    value={item.status}
                    onChange={(e) => updateFeatureStatus(item.id, e.target.value as FeatureStatus)}
                    className="rounded-2xl border border-border bg-surface-raised/84 px-3 py-2 text-xs text-content focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="submitted">submitted</option>
                    <option value="triage">triage</option>
                    <option value="planned">planned</option>
                    <option value="in-progress">in-progress</option>
                    <option value="done">done</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}
